import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import BuhotextCampaignsAPI from '../../api/buhotextCampaigns';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  devices: [],
  smsList: [],
  smsMeta: {},
  campaignsList: [],
  campaignsMeta: {},
  uiFlags: {
    isFetchingItem: false,
    isFetching: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    isSending: false,
    isFetchingStats: false,
    isFetchingDevices: false,
    isFetchingSMS: false,
    isDeletingSMS: false,
    isFetchingCampaigns: false,
    isCreatingCampaign: false,
    isDeletingCampaign: false,
  },
};

export const getters = {
  getCampaigns($state) {
    return $state.records || [];
  },
  getCampaign: $state => id => {
    const records = $state.records || [];
    return records.find(record => record.id === Number(id));
  },
  getDevices($state) {
    return $state.devices || [];
  },
  getSMSList($state) {
    return $state.smsList || [];
  },
  getSMSMeta($state) {
    return $state.smsMeta || {};
  },
  getCampaignsList($state) {
    return $state.campaignsList || [];
  },
  getCampaignsMeta($state) {
    return $state.campaignsMeta || {};
  },
  getUIFlags($state) {
    return $state.uiFlags || {};
  },
};

export const actions = {
  get: async function getCampaigns(
    { commit },
    { page = 1, resultsPerPage = 25 } = {}
  ) {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isFetching: true });
    try {
      const response = await BuhotextCampaignsAPI.listCampaigns(
        page,
        resultsPerPage
      );
      // API de 66text retorna { data: [...], meta: {...} }
      commit(types.SET_BUHOTEXT_CAMPAIGNS, response.data.data || []);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isFetching: false });
    }
  },
  getSingleCampaign: async function getCampaignById({ commit }, campaignId) {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await BuhotextCampaignsAPI.getCampaign(campaignId);
      // API de 66text retorna { data: {...} }
      commit(types.ADD_BUHOTEXT_CAMPAIGN, response.data.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isFetchingItem: false });
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isCreating: true });
    try {
      const response = await BuhotextCampaignsAPI.createCampaign(campaignObj);
      // API de 66text retorna { data: { id: ... } }
      return response.data.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isCreating: false });
    }
  },
  send: async function sendCampaign({ commit }, campaignId) {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isSending: true });
    try {
      const response = await BuhotextCampaignsAPI.send(campaignId);
      commit(types.EDIT_BUHOTEXT_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isSending: false });
    }
  },
  fetchStats: async function getCampaignStats({ commit }, campaignId) {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isFetchingStats: true });
    try {
      const response = await BuhotextCampaignsAPI.stats(campaignId);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isFetchingStats: false });
    }
  },
  fetchDevices: async function getAvailableDevices({ commit }) {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isFetchingDevices: true });
    try {
      const response = await BuhotextCampaignsAPI.availableDevices();
      commit(types.SET_BUHOTEXT_DEVICES, response.data.data || []);
      return response.data.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, {
        isFetchingDevices: false,
      });
    }
  },
  createDevice: async function createNewDevice({ commit }, { name }) {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isCreating: true });
    try {
      const response = await BuhotextCampaignsAPI.createDevice(name);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isCreating: false });
    }
  },
  updateDevice: async function updateExistingDevice(
    { commit },
    { deviceId, name }
  ) {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isUpdating: true });
    try {
      const response = await BuhotextCampaignsAPI.updateDevice(deviceId, name);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isUpdating: false });
    }
  },
  deleteDevice: async function removeDevice({ commit }, deviceId) {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isDeleting: true });
    try {
      await BuhotextCampaignsAPI.deleteDevice(deviceId);
      return true;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isDeleting: false });
    }
  },
  sendDirectSMS: async function sendSMS(
    { commit },
    {
      deviceId,
      simSubscriptionId,
      content,
      phoneNumbers,
      isScheduled,
      scheduledDatetime,
    }
  ) {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isSending: true });
    try {
      const response = await BuhotextCampaignsAPI.sendDirectSMS(
        deviceId,
        simSubscriptionId,
        content,
        phoneNumbers,
        isScheduled,
        scheduledDatetime
      );
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isSending: false });
    }
  },
  fetchSMSList: async ({ commit }, { page = 1, resultsPerPage = 25 } = {}) => {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isFetchingSMS: true });
    try {
      const response = await BuhotextCampaignsAPI.listSMS(page, resultsPerPage);
      commit('SET_BUHOTEXT_SMS_LIST', response.data.data);
      commit('SET_BUHOTEXT_SMS_META', response.data.meta);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isFetchingSMS: false });
    }
  },
  deleteSMS: async ({ commit }, smsId) => {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isDeletingSMS: true });
    try {
      await BuhotextCampaignsAPI.deleteSMS(smsId);
      return true;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isDeletingSMS: false });
    }
  },
  fetchCampaignsList: async (
    { commit },
    { page = 1, resultsPerPage = 25 } = {}
  ) => {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, {
      isFetchingCampaigns: true,
    });
    try {
      const response = await BuhotextCampaignsAPI.listCampaigns(
        page,
        resultsPerPage
      );
      commit('SET_BUHOTEXT_CAMPAIGNS_LIST', response.data.data);
      commit('SET_BUHOTEXT_CAMPAIGNS_META', response.data.meta);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, {
        isFetchingCampaigns: false,
      });
    }
  },
  createCampaign: async ({ commit }, campaignData) => {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isCreatingCampaign: true });
    try {
      const response = await BuhotextCampaignsAPI.createCampaign(campaignData);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, {
        isCreatingCampaign: false,
      });
    }
  },
  deleteCampaign: async ({ commit }, campaignId) => {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, {
      isDeletingCampaign: true,
    });
    try {
      await BuhotextCampaignsAPI.deleteCampaign(campaignId);
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, {
        isDeletingCampaign: false,
      });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isUpdating: true });
    try {
      const response = await BuhotextCampaignsAPI.updateCampaign(id, updateObj);
      // API de 66text retorna { data: { id: ... } }
      return response.data.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isDeleting: true });
    try {
      await BuhotextCampaignsAPI.deleteCampaign(id);
      // Recarga la lista después de borrar
      commit(types.DELETE_BUHOTEXT_CAMPAIGN, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_BUHOTEXT_CAMPAIGNS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.ADD_BUHOTEXT_CAMPAIGN]: MutationHelpers.setSingleRecord,
  [types.SET_BUHOTEXT_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_BUHOTEXT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_BUHOTEXT_CAMPAIGN]: MutationHelpers.destroy,
  SET_BUHOTEXT_DEVICES($state, devices) {
    $state.devices = devices;
  },
  SET_BUHOTEXT_SMS_LIST($state, smsList) {
    $state.smsList = smsList;
  },
  SET_BUHOTEXT_SMS_META($state, meta) {
    $state.smsMeta = meta;
  },
  SET_BUHOTEXT_CAMPAIGNS_LIST($state, campaignsList) {
    $state.campaignsList = campaignsList;
  },
  SET_BUHOTEXT_CAMPAIGNS_META($state, meta) {
    $state.campaignsMeta = meta;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
